import type { LigneDuPourquoi, LigneFlottante, Vivier } from '../partage/api/types';
import { proprietesEnMots } from '../partage/vocabulaire';

/**
 * Maintenant en étages, à l'ordinateur (chantier maintenant-en-etages, modification 01 ; maquette
 * du carnet `maintenant.html`, décision 22 et décisions d'Igor du 2026-10-21) : la réponse du
 * serveur découpée telle quelle, sans rien recalculer de la flottaison ni reclasser une ligne.
 *
 * - **Surface** : les prioritaires, 3 au plus (le serveur n'en rend pas plus, jamais filtrés) ;
 * - **Léger** : les 3 premières de Pendant ce temps, celles qui flottent le mieux ;
 * - **Plus lourd** : les 3 suivantes ;
 * - **Sous l'eau** : le reste de Pendant ce temps, puis les tâches pliées sous un galet (« + 2
 *   autres avant le 12 octobre » : un galet ne montre que la sienne), puis ce que le serveur
 *   range sous l'eau (ses lignes n'arrivent qu'avec `toutVoir`, son nombre toujours).
 */

/** Combien de galets un étage porte au plus. */
export const PAR_ETAGE = 3;

/** Combien de noms Sous l'eau montre replié, avant « + N autres ». */
export const NOMS_SOUS_L_EAU = 4;

export interface EtagesDeMaintenant {
  surface: LigneDuPourquoi[];
  leger: LigneFlottante[];
  plusLourd: LigneFlottante[];
  /** Les lignes sous l'eau connues du client, dans l'ordre. */
  sousLEau: LigneDuPourquoi[];
  /** Combien sont sous l'eau en tout, celles dont les lignes ne sont pas encore arrivées comprises. */
  nombreSousLEau: number;
}

export function etagesDeMaintenant(
  v: Pick<Vivier, 'prioritaire' | 'pendantCeTemps' | 'nombreSousLEau' | 'sousLEau'>
): EtagesDeMaintenant {
  const leger = v.pendantCeTemps.slice(0, PAR_ETAGE);
  const plusLourd = v.pendantCeTemps.slice(PAR_ETAGE, 2 * PAR_ETAGE);
  const reste = v.pendantCeTemps.slice(2 * PAR_ETAGE);
  const pliees = [...leger, ...plusLourd].flatMap((l) => l.pliees);
  return {
    surface: v.prioritaire.slice(0, PAR_ETAGE),
    leger,
    plusLourd,
    sousLEau: [...reste, ...pliees, ...v.sousLEau],
    nombreSousLEau: reste.length + pliees.length + v.nombreSousLEau,
  };
}

/**
 * Les noms que Sous l'eau montre : replié, 4 au plus, puis « + N autres » ; déplié, tous ceux qui
 * sont arrivés. Ce qui n'est pas encore arrivé (le serveur relit avec `toutVoir`) reste compté.
 */
export function nomsSousLEau(
  lignes: LigneDuPourquoi[],
  nombre: number,
  ouvert: boolean
): { montres: LigneDuPourquoi[]; autres: number } {
  const montres = ouvert ? lignes : lignes.slice(0, NOMS_SOUS_L_EAU);
  return { montres, autres: Math.max(0, nombre - montres.length) };
}

/**
 * Ce qu'un galet dit en petit, sous son titre : « 10 min · marge de 2 jours · prévue jeudi », sa
 * durée, son pourquoi et sa mention, tous venus du serveur. Ce qui manque se tait ; la durée ne se
 * dit pas deux fois quand le pourquoi commence déjà par elle (« 10 min, sans bouger »).
 */
export function mentionDuGalet(ligne: LigneDuPourquoi): string {
  const pourquoi = ligne.pourquoi || null;
  const duree = proprietesEnMots(ligne, ['duree']).filter((d) => !pourquoi?.startsWith(d));
  return [...duree, pourquoi, ligne.prevue].filter((m): m is string => Boolean(m)).join(' · ');
}
